import React from 'react'
import type { ApiErrorLike } from '@/lib/apiError'
import { sdk } from '@/lib/sdk'
import { rememberReturnTo } from '@/lib/returnTo'
import type { UpvoteSort, UpvoteStatus, UpvoteTopic } from '@/types/upvotes'
import { leavePageTo } from '@/lib/player/leave'

/** Every state, in the order of the board. */
export const STATUSES: UpvoteStatus[] = ['OPEN', 'PLANNED', 'IN_PROGRESS', 'DONE', 'ARCHIVED']

/** The columns of the board view (archived lives in the list filter). */
export const COLUMN_STATUSES: UpvoteStatus[] = ['OPEN', 'PLANNED', 'IN_PROGRESS', 'DONE']

/** States that still take votes (and are valid merge targets). */
export const VOTABLE_STATUSES: UpvoteStatus[] = ['OPEN', 'PLANNED', 'IN_PROGRESS']

export const SORTS: UpvoteSort[] = ['top', 'new', 'trending']

export const PAGE_SIZE = 20

/** Badge tone per state. */
export const STATUS_TONE: Record<UpvoteStatus, string> = {
  OPEN: 'bg-gray-100 text-gray-700',
  PLANNED: 'bg-blue-50 text-blue-700',
  IN_PROGRESS: 'bg-amber-50 text-amber-800',
  DONE: 'bg-green-50 text-green-700',
  ARCHIVED: 'bg-gray-100 text-gray-500'
}

export const topicPath = (spacePath: string, topicId: string) => `${spacePath}/${topicId}`

export const apiMessage = (err: unknown, fallback: string): string =>
  (err as ApiErrorLike)?.response?.data?.message || fallback

export const apiStatus = (err: unknown): number | undefined => (err as ApiErrorLike)?.response?.status

/** The topic with a vote result merged in. */
export const withVote = (topic: UpvoteTopic, result: { voteCount: number; hasVoted: boolean }): UpvoteTopic =>
  ({ ...topic, voteCount: result.voteCount, hasVoted: result.hasVoted })

/**
 * A visitor (no login, public board) tapped vote, comment or "new topic": the
 * community's login or signup opens on this same page (`?login=1` /
 * `?signup=1`, read by the Header), and the login brings them back here.
 */
export const goToAuth = (mode: 'login' | 'signup') => {
  rememberReturnTo()
  leavePageTo(`${window.location.pathname}?${mode}=1`)
}

/** Words of a title, the way the server counts them for "similar topics". */
export const wordCount = (title: string) => title.trim().split(/\s+/).filter(Boolean).length

export const formatDay = (iso: string, locale: string) => {
  try {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(new Date(iso))
  } catch {
    return ''
  }
}

/** An upload no topic took: the file and the asset go away. Failures are not worth a message. */
export const discardUpload = (mediaId: string) => { void sdk.media.delete(mediaId).catch(() => undefined) }

export interface UpvotesSpaceConfig {
  requireApproval: boolean
}

export const defaultUpvotesConfig = (): UpvotesSpaceConfig => ({ requireApproval: false })

/** Viewport at least `px` wide, kept in sync on resize. */
export const useMinWidth = (px: number) => {
  const query = `(min-width: ${px}px)`
  const [matches, setMatches] = React.useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  React.useEffect(() => {
    const media = window.matchMedia(query)
    const onChange = () => setMatches(media.matches)
    onChange()
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [query])
  return matches
}
